import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { NoirBg } from "@/components/NoirBg";
import { FeedComposer } from "@/components/feed/FeedComposer";
import { Stories } from "@/components/feed/Stories";
import { RankingCard } from "@/components/feed/RankingCard";
import { PhotoStrip } from "@/components/feed/PhotoStrip";
import { PostCard } from "@/components/feed/PostCard";
import { Avatar } from "@/components/community/ChatList";
import { useCommunitySocket } from "@/hooks/useCommunitySocket";
import { Loader2, Layers, Bell, Search, MessagesSquare, RefreshCw, Store, Wand2, Users, Copy, Radar } from "lucide-react";

const FILTERS = [
  { key: "", label: "Todo" },
  { key: "anuncio", label: "Anuncios" },
  { key: "producto", label: "Productos" },
];

const NOTIF_TEXT = {
  mention: "te etiquetó",
  comment: "comentó tu publicación",
  reply: "respondió tu comentario",
  share: "compartió tu publicación",
  comment_like: "le gustó tu comentario",
  story_reply: "respondió tu historia",
  friend_request: "te envió una solicitud de amistad",
  friend_accept: "aceptó tu solicitud de amistad",
  follow: "empezó a seguirte",
};

/** Qué hizo esa persona, en palabras sencillas. */
const notifText = (n) => {
  if (n.kind === "story_reaction") return `reaccionó ${n.text} a tu historia`;
  return NOTIF_TEXT[n.kind] || "reaccionó a tu publicación";
};

export default function Feed() {
  const [me, setMe] = useState(null);
  const [posts, setPosts] = useState(null);
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [notifs, setNotifs] = useState({ notifications: [], unread: 0 });
  const [bell, setBell] = useState(false);
  const [fresh, setFresh] = useState(false);
  const [storiesKey, setStoriesKey] = useState(0);
  const [limited, setLimited] = useState(false);
  const [pending, setPending] = useState(0);

  const load = useCallback(async (filters = {}) => {
    try {
      const { data } = await userApi.get("/feed", { params: { kind, q, ...filters } });
      setPosts(data.posts);
      setLimited(!!data.limited);
      setFresh(false);
    } catch (e) { toast.error(apiError(e, "No se pudo cargar el muro")); setPosts([]); }
  }, [kind, q]);

  const loadNotifs = useCallback(async () => {
    try {
      const { data } = await userApi.get("/feed/notifications");
      setNotifs(data);
    } catch (e) { console.warn("No se pudieron cargar las notificaciones:", e); }
  }, []);

  useEffect(() => {
    userApi.get("/community/me").then(({ data }) => setMe(data.profile)).catch(() => {});
    userApi.get("/social/friends")
      .then(({ data }) => setPending(data.incoming?.length || 0)).catch(() => {});
  }, []);
  useEffect(() => { load(); loadNotifs(); }, [load, loadNotifs]);

  useCommunitySocket((ev) => {
    if (ev.event === "feed") setFresh(true);
    if (ev.event === "stories") setStoriesKey((k) => k + 1);
    if (ev.event === "notification") loadNotifs();
  });

  const openBell = async () => {
    setBell((v) => !v);
    if (!bell && notifs.unread > 0) {
      await userApi.post("/feed/notifications/read").catch(() => {});
      setNotifs((n) => ({ ...n, unread: 0 }));
    }
  };

  return (
    <div className="min-h-screen noir-bg theme-noir" style={{ backgroundColor: "#050507" }}>
      <NoirBg />

      <header className="sticky top-0 z-30 border-b border-white/10 backdrop-blur-xl"
        style={{ background: "rgba(10,3,4,0.55)" }}>
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/logo-digital-forge.jpg" alt="Digital Forge" data-testid="brand-logo"
              className="w-9 h-9 rounded-md object-cover shrink-0 border border-[#FF8A2B]/40"
              style={{ boxShadow: "0 0 14px rgba(255,138,43,0.28)" }} />
            <div className="leading-tight">
              <div className="font-heading font-bold text-sm text-white">Digital Forge</div>
              <div className="text-[10px] text-white/40 tracking-[0.14em]">MURO</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/amigos" data-testid="feed-to-friends"
              className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5 relative">
              <Users className="w-3.5 h-3.5" /> Amigos
              {pending > 0 && (
                <span data-testid="feed-friends-count"
                  className="absolute -top-1 -right-1 text-[10px] font-bold px-1.5 rounded-full bg-red-600 text-white">
                  {pending}
                </span>
              )}
            </Link>
            <Link to="/mercado" data-testid="feed-to-market"
              className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
              <Store className="w-3.5 h-3.5" /> Mercado
            </Link>
            <Link to="/clonador" data-testid="feed-to-cloner"
              className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
              <Copy className="w-3.5 h-3.5" /> Clonador
            </Link>
            <Link to="/matrix" data-testid="feed-to-matrix"
              className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5 text-red-300">
              <Radar className="w-3.5 h-3.5" /> Matrix
            </Link>
            <Link to="/comunidad" data-testid="feed-to-community"
              className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
              <MessagesSquare className="w-3.5 h-3.5" /> Chats
            </Link>
            <Link to="/panel" data-testid="feed-to-editor"
              className="glass-btn glass-btn-red px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
              <Wand2 className="w-3.5 h-3.5" /> Editor
            </Link>
            <div className="relative">
              <button data-testid="feed-bell" onClick={openBell}
                className="glass-btn w-10 h-10 rounded-xl flex items-center justify-center relative">
                <Bell className="w-4 h-4" />
                {notifs.unread > 0 && (
                  <span data-testid="feed-bell-count"
                    className="absolute -top-1 -right-1 text-[10px] font-bold px-1.5 rounded-full bg-red-600 text-white">
                    {notifs.unread}
                  </span>
                )}
              </button>
              {bell && (
                <div className="absolute right-0 top-12 glass-card p-2 w-72 max-h-80 overflow-y-auto z-40"
                  data-testid="feed-notifications">
                  {notifs.notifications.length === 0 && (
                    <div className="text-xs text-white/50 px-2 py-3">Sin novedades todavía.</div>
                  )}
                  {notifs.notifications.map((n) => (
                    <div key={n.notif_id} className="px-2.5 py-2 text-xs text-white/80 border-b border-white/6">
                      <span className="text-white font-semibold">{n.actor_name}</span>{" "}
                      {notifText(n)}
                      {n.text && <div className="text-white/45 truncate">{n.text}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Link to={me ? `/perfil/${me.key}` : "/panel"} data-testid="feed-my-profile">
              <Avatar person={me} size={34} />
            </Link>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-4 py-5 space-y-4">
        {limited && (
          <div className="glass-card p-4 text-xs text-amber-200/90" data-testid="feed-limited">
            Estás viendo la comunidad de forma limitada. Vincula tu Telegram en «Mi cuenta»
            para ver todo el muro, los perfiles y las fotos sin restricciones.
          </div>
        )}
        <Stories me={me} refreshKey={storiesKey} />
        <RankingCard />
        <FeedComposer me={me} onPosted={() => load()} />
        <PhotoStrip refreshKey={posts?.length || 0} />

        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => (
            <button key={f.key} data-testid={`feed-filter-${f.key || "todo"}`} onClick={() => setKind(f.key)}
              className={`glass-btn ${kind === f.key ? "glass-btn-red" : ""} px-3 py-1.5 rounded-xl text-xs shrink-0 whitespace-nowrap`}>
              {f.label}
            </button>
          ))}
          <div className="flex-1 min-w-[160px] flex items-center gap-2">
            <input data-testid="feed-search" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Buscar en el muro"
              className="glass-field flex-1 px-3 py-1.5 rounded-xl text-xs text-white placeholder-white/35 outline-none" />
            <button data-testid="feed-search-btn" onClick={() => load()}
              className="glass-btn w-8 h-8 rounded-xl flex items-center justify-center">
              <Search className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {fresh && (
          <button data-testid="feed-new-posts" onClick={() => load()}
            className="glass-btn glass-btn-red w-full py-2 rounded-xl text-xs flex items-center justify-center gap-2">
            <RefreshCw className="w-3.5 h-3.5" /> Hay publicaciones nuevas — ver ahora
          </button>
        )}

        {posts === null ? (
          <Loader2 className="w-6 h-6 animate-spin text-red-400 mx-auto mt-10" />
        ) : posts.length === 0 ? (
          <div className="glass-card p-6 text-sm text-white/60" data-testid="feed-empty">
            Todavía no hay publicaciones. ¡Rompe el hielo con la primera!
          </div>
        ) : (
          <div className="space-y-4" data-testid="feed-list">
            {posts.map((p, i) => (
              <div key={p.post_id} className="noir-rise"
                style={{ animationDelay: `${Math.min(i, 6) * 70}ms` }}>
                <PostCard post={p} me={me} onChanged={() => load()} />
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
